import { getToken, removeToken } from './auth';
import { getLanguage } from './i18n';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const WORKSPACE_STORAGE_KEY = 'eagle_workspace_id';

let workspaceIdForApi = (() => {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
})();

export const setWorkspaceIdForApi = (id) => {
  if (workspaceIdForApi !== id) {
    invalidateMediasCache();
  }
  workspaceIdForApi = id;
};

export const clearWorkspaceIdForApi = () => {
  workspaceIdForApi = null;
  invalidateMediasCache();
};

export const getWorkspaceIdForApi = () => workspaceIdForApi;

let mediasInFlight = null;
let mediasCachedAt = 0;
const MEDIAS_CACHE_MS = 30_000;

export const invalidateMediasCache = () => {
  mediasInFlight = null;
  mediasCachedAt = 0;
};

const redirectToLoginAfterUnauthorized = () => {
  const params = new URLSearchParams(window.location.search);
  const desktopReturn =
    params.get('desktop_return') || sessionStorage.getItem('eagle_desktop_return') || '';
  if (desktopReturn) {
    window.location.href = `/login?desktop_return=${encodeURIComponent(desktopReturn)}`;
  } else {
    window.location.href = '/login';
  }
};

export const publicApiRequest = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }

  return data;
};

export const validateAuthSession = async () => {
  const token = getToken();
  if (!token) {
    return { kind: 'invalid' };
  }
  let response;
  try {
    response = await fetch(`${API_URL}/auth/validate`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error('Network error:', error);
    return { kind: 'transient' };
  }
  if (response.status === 401) {
    removeToken();
    return { kind: 'invalid' };
  }
  if (response.ok) {
    return { kind: 'ok' };
  }
  return { kind: 'transient' };
};

export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  const language = getLanguage();
  if (language) {
    headers['Accept-Language'] = language;
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    const msg = data.error || data.message || text || 'Request failed';
    const err = new Error(msg);
    err.status = response.status;
    if (data && Object.prototype.hasOwnProperty.call(data, 'data')) {
      err.responseData = data.data;
    }
    throw err;
  }

  return data;
};

export const login = async (email, password) => {
  // Must use publicApiRequest: apiRequest treats 401 as expired session and redirects to /login,
  // which breaks displaying "Invalid credentials" on failed login.
  return publicApiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
};

export const sendMagicLinkCode = async (email) => {
  return publicApiRequest('/auth/send-code', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export const validateMagicLink = async (email, code) => {
  return publicApiRequest('/auth/validate', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
};

export const getAdminWorkspaceOverview = async () => {
  return apiRequest('/admin/workspaces/overview');
};

export const transferWorkspaceOwner = async (workspaceId, userId) => apiRequest(
  `/admin/workspaces/${encodeURIComponent(workspaceId)}/owner`,
  {
    method: 'PATCH',
    body: JSON.stringify({ user_id: userId }),
  },
);

export const downloadAdminWorkspaceBackup = async (workspaceId) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const response = await fetch(
    `${API_URL}/admin/workspaces/${encodeURIComponent(workspaceId)}/download`,
    { headers }
  );
  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    let msg = text || 'Request failed';
    try {
      const j = JSON.parse(text);
      if (j.error) msg = j.error;
      else if (j.message) msg = j.message;
    } catch (_) {
      /* use msg */
    }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const cd = response.headers.get('Content-Disposition');
  let filename = 'workspace-backup.zip';
  if (cd && cd.includes('filename=')) {
    const m = cd.match(/filename="([^"]+)"/) || cd.match(/filename=([^;\s]+)/);
    if (m) {
      filename = m[1].replace(/"/g, '');
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const parseAdminBackupResponse = (xhr) => {
  const text = xhr.responseText || '';
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (xhr.status < 200 || xhr.status >= 300) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }
  if (xhr.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  if (xhr.status < 200 || xhr.status >= 300) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }
  return data;
};

export const uploadAdminBackupRestore = (file, onProgress) => new Promise((resolve, reject) => {
  const form = new FormData();
  form.append('backup', file);
  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${API_URL}/admin/workspaces/restore/upload`);
  const token = getToken();
  if (token) {
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  }
  if (workspaceIdForApi) {
    xhr.setRequestHeader('X-Workspace-ID', workspaceIdForApi);
  }
  if (onProgress) {
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        onProgress(ev.loaded, ev.total);
      }
    };
  }
  xhr.onload = () => {
    try {
      resolve(parseAdminBackupResponse(xhr));
    } catch (err) {
      reject(err);
    }
  };
  xhr.onerror = () => reject(new Error('Request failed'));
  xhr.send(form);
});

export const previewAdminBackupRestore = async (uploadId) => apiRequest('/admin/workspaces/restore/preview', {
  method: 'POST',
  body: JSON.stringify({ upload_id: uploadId }),
});

export const restoreAdminBackup = async (uploadId, workspaceName, userId) => apiRequest('/admin/workspaces/restore', {
  method: 'POST',
  body: JSON.stringify({
    upload_id: uploadId,
    workspace_name: workspaceName,
    user_id: userId,
  }),
});

export const getAdminBackupRestoreJob = async (jobId) => apiRequest(`/admin/workspaces/restore/jobs/${encodeURIComponent(jobId)}`);

export const getUsers = async () => {
  return apiRequest('/users');
};

export const getUser = async (id) => {
  return apiRequest(`/users/${id}`);
};

export const createUser = async (userData) => {
  return apiRequest('/users', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
};

export const updateUser = async (id, userData) => {
  return apiRequest(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
};

export const deleteUser = async (id) => {
  return apiRequest(`/users/${id}`, {
    method: 'DELETE',
  });
};

export const approveUser = async (id) => {
  return apiRequest(`/users/${id}/approve`, {
    method: 'POST',
  });
};

export const blockUser = async (id) => {
  return apiRequest(`/users/${id}/block`, {
    method: 'POST',
  });
};

export const getProducts = async (query = {}) => {
  const q = new URLSearchParams();
  const s = String(query.q || '').trim();
  if (s) q.set('q', s);
  const typeFilter = String(query.type || '').trim();
  if (typeFilter && typeFilter !== 'ALL') q.set('type', typeFilter);
  const productTypeId = String(query.product_type_id || '').trim();
  if (productTypeId) q.set('product_type_id', productTypeId);
  const categoryId = String(query.category_id || '').trim();
  if (categoryId) q.set('category_id', categoryId);
  const statusFilter = String(query.status || '').trim();
  if (statusFilter && statusFilter !== 'ALL') q.set('status', statusFilter);
  if (query.sort) q.set('sort', String(query.sort));
  if (query.order) q.set('order', String(query.order));
  if (query.page) q.set('page', String(query.page));
  if (query.limit) q.set('limit', String(query.limit));
  const qs = q.toString();
  const res = await apiRequest(`/products${qs ? `?${qs}` : ''}`);
  const data = res.data || {};
  const products = data.products ?? [];
  return {
    ...res,
    data: {
      products,
      total: data.total ?? products.length,
      page: data.page ?? 1,
      limit: data.limit ?? products.length,
    },
  };
};

export const getProductCatalog = async (id) => {
  return apiRequest(`/products/${encodeURIComponent(id)}/catalog`);
};

export const syncProductOptions = async (id, payload) => {
  return apiRequest(`/products/${encodeURIComponent(id)}/options`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const purgeLegacySKUs = async (id) => {
  return apiRequest(`/products/${encodeURIComponent(id)}/skus/purge-legacy`, {
    method: 'POST',
  });
};

export const purgeAllLegacySKUs = async () => {
  return apiRequest('/products/skus/purge-legacy-all', {
    method: 'POST',
  });
};

export const setProductTypes = async (id, payload) => {
  return apiRequest(`/products/${encodeURIComponent(id)}/types`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getProductEditHistory = async (id) => {
  const res = await apiRequest(`/products/${encodeURIComponent(id)}/edit-history`);
  const list = Array.isArray(res.data) ? res.data : res.data?.events ?? [];
  return { ...res, data: list };
};

export const getProductTypes = async (query = {}) => {
  const q = new URLSearchParams();
  const search = String(query.q || '').trim();
  if (search) q.set('q', search);
  if (query.limit != null) q.set('limit', String(query.limit));
  const qs = q.toString();
  const res = await apiRequest(`/products/types${qs ? `?${qs}` : ''}`);
  const list = Array.isArray(res.data) ? res.data : [];
  return { ...res, data: list };
};

export const createProductType = async (payload) => {
  return apiRequest('/products/types', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateProductType = async (id, payload) => {
  return apiRequest(`/products/types/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteProductType = async (id) => {
  return apiRequest(`/products/types/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
};

export const getCategoryTree = async () => {
  return apiRequest('/categories/tree');
};

export const getCategories = async (query = {}) => {
  const q = new URLSearchParams();
  const search = String(query.q || '').trim();
  if (search) q.set('q', search);
  const qs = q.toString();
  return apiRequest(`/categories${qs ? `?${qs}` : ''}`);
};

export const getCategory = async (id) => {
  return apiRequest(`/categories/${encodeURIComponent(id)}`);
};

export const createCategory = async (payload) => {
  return apiRequest('/categories', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateCategory = async (id, payload) => {
  return apiRequest(`/categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteCategory = async (id) => {
  return apiRequest(`/categories/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
};

export const reorderCategories = async (payload) => {
  return apiRequest('/categories/reorder', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const moveCategory = async (id, payload) => {
  return apiRequest(`/categories/${encodeURIComponent(id)}/move`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getCategoryProducts = async (id, query = {}) => {
  const q = new URLSearchParams();
  const search = String(query.q || '').trim();
  if (search) q.set('q', search);
  if (query.sort) q.set('sort', String(query.sort));
  if (query.order) q.set('order', String(query.order));
  if (query.page) q.set('page', String(query.page));
  if (query.limit) q.set('limit', String(query.limit));
  const qs = q.toString();
  return apiRequest(`/categories/${encodeURIComponent(id)}/products${qs ? `?${qs}` : ''}`);
};

export const getCategoryAttributes = async (id) => {
  return apiRequest(`/categories/${encodeURIComponent(id)}/attributes`);
};

export const setCategoryAttributes = async (id, payload) => {
  return apiRequest(`/categories/${encodeURIComponent(id)}/attributes`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getInheritedAttributes = async (categoryId) => {
  const res = await apiRequest(`/categories/${encodeURIComponent(categoryId)}/inherited-attributes`);
  const payload = res.data ?? {};
  const attributes = payload.attributes ?? payload.Attributes ?? [];
  return { ...res, data: { attributes } };
};

export const getInheritedVariableCosts = async (categoryId) => {
  const res = await apiRequest(`/categories/${encodeURIComponent(categoryId)}/inherited-variable-costs`);
  const payload = res.data ?? {};
  const defaults = payload.defaults ?? payload.Defaults ?? [];
  return { ...res, data: { defaults } };
};

export const getAttributeSpecifications = async (query = {}) => {
  const q = new URLSearchParams();
  const search = String(query.q || '').trim();
  if (search) q.set('q', search);
  if (query.limit != null) q.set('limit', String(query.limit));
  const qs = q.toString();
  return apiRequest(`/attribute-specifications${qs ? `?${qs}` : ''}`);
};

export const createAttributeSpecification = async (payload) => {
  return apiRequest('/attribute-specifications', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateAttributeSpecification = async (id, payload) => {
  return apiRequest(`/attribute-specifications/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteAttributeSpecification = async (id) => {
  return apiRequest(`/attribute-specifications/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
};

export const setProductCategories = async (id, payload) => {
  return apiRequest(`/products/${encodeURIComponent(id)}/categories`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const setProductAttributeValues = async (id, payload) => {
  return apiRequest(`/products/${encodeURIComponent(id)}/attribute-values`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getCustomizationFields = async (productId) => {
  const res = await apiRequest(`/products/${encodeURIComponent(productId)}/customization-fields`);
  const list = Array.isArray(res.data) ? res.data : [];
  return { ...res, data: list };
};

export const createCustomizationField = async (productId, payload) => {
  return apiRequest(`/products/${encodeURIComponent(productId)}/customization-fields`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateCustomizationField = async (productId, fieldId, payload) => {
  return apiRequest(
    `/products/${encodeURIComponent(productId)}/customization-fields/${encodeURIComponent(fieldId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  );
};

export const deleteCustomizationField = async (productId, fieldId) => {
  return apiRequest(
    `/products/${encodeURIComponent(productId)}/customization-fields/${encodeURIComponent(fieldId)}`,
    {
      method: 'DELETE',
    },
  );
};

export const getProduct = async (id) => {
  return apiRequest(`/products/${id}`);
};

export const createProduct = async (productData) => {
  return apiRequest('/products', {
    method: 'POST',
    body: JSON.stringify(productData),
  });
};

export const updateProduct = async (id, productData) => {
  return apiRequest(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(productData),
  });
};

export const deleteProduct = async (id) => {
  return apiRequest(`/products/${id}`, {
    method: 'DELETE',
  });
};

export const getSKUs = async (productId) => {
  const res = await apiRequest(`/products/${productId}/skus`);
  const list = res.data?.skus ?? (Array.isArray(res.data) ? res.data : []);
  return { ...res, data: list };
};

export const getSKU = async (skuId) => {
  return apiRequest(`/skus/${skuId}`);
};

export const createSKU = async (productId, skuData) => {
  return apiRequest(`/products/${productId}/skus`, {
    method: 'POST',
    body: JSON.stringify(skuData),
  });
};

export const updateSKU = async (skuId, skuData) => {
  return apiRequest(`/skus/${skuId}`, {
    method: 'PUT',
    body: JSON.stringify(skuData),
  });
};

export const deleteSKU = async (skuId) => {
  return apiRequest(`/skus/${skuId}`, {
    method: 'DELETE',
  });
};

/** Multipart to workspace API — must not set Content-Type (browser sets boundary). */
async function workspaceMultipartRequest(endpoint, formData, method = 'POST') {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers,
      body: formData,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }

  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    const msg = data.error || data.message || text || 'Request failed';
    const err = new Error(msg);
    err.status = response.status;
    if (data && Object.prototype.hasOwnProperty.call(data, 'data')) {
      err.responseData = data.data;
    }
    throw err;
  }

  return data;
}

async function workspaceMultipartPost(endpoint, formData) {
  return workspaceMultipartRequest(endpoint, formData, 'POST');
}

export const listCatalogs = async (query = {}) => {
  const p = new URLSearchParams();
  if (query.sort) p.set('sort', String(query.sort));
  if (query.order) p.set('order', String(query.order));
  if (typeof query.page === 'number') p.set('page', String(query.page));
  if (typeof query.page_size === 'number') p.set('page_size', String(query.page_size));
  const s = String(query.q || '').trim();
  if (s) p.set('q', s);
  if (query.status_filter === 'none') p.set('status_filter', 'none');
  const statuses = query.status;
  if (Array.isArray(statuses)) {
    for (const st of statuses) {
      const t = String(st).trim();
      if (t) p.append('status', t);
    }
  }
  const qs = p.toString();
  return apiRequest(`/catalogs${qs ? `?${qs}` : ''}`);
};

export const createCatalog = async (payload = {}) => {
  return apiRequest('/catalogs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const getCatalog = async (id) => {
  return apiRequest(`/catalogs/${id}`);
};

export const getCatalogPrintHtml = async (id) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const response = await fetch(`${API_URL}/catalogs/${encodeURIComponent(id)}/print`, {
    method: 'GET',
    headers,
  });
  const text = await response.text();
  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    let msg = text;
    try {
      const data = JSON.parse(text);
      msg = data.error || data.message || text;
    } catch {
      /* keep text */
    }
    throw new Error(msg || 'Print unavailable');
  }
  return text;
};

export const getCatalogEditHistory = async (id) => {
  const res = await apiRequest(`/catalogs/${encodeURIComponent(id)}/edit-history`);
  return res?.data ?? res;
};

export const updateCatalog = async (id, payload) => {
  return apiRequest(`/catalogs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteCatalog = async (id) => {
  return apiRequest(`/catalogs/${id}`, {
    method: 'DELETE',
  });
};

export const cloneCatalog = async (id) => {
  return apiRequest(`/catalogs/${id}/clone`, {
    method: 'POST',
  });
};

export const replaceCatalogComposition = async (id, payload) => {
  return apiRequest(`/catalogs/${id}/composition`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getCatalogDocument = async (id) => {
  return apiRequest(`/catalogs/${id}/document`);
};

export const updateCatalogDocument = async (id, payload) => {
  return apiRequest(`/catalogs/${id}/document`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getCatalogTemplateDocument = async (id) => {
  return apiRequest(`/catalog/templates/${id}/document`);
};

export const updateCatalogTemplateDocument = async (id, payload) => {
  return apiRequest(`/catalog/templates/${id}/document`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getCatalogSettings = async () => {
  return apiRequest('/catalogs/settings');
};

export const updateCatalogSettings = async (payload) => {
  return apiRequest('/catalogs/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const listCatalogTemplates = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.include_content) {
    params.set('include_content', 'true');
  }
  const qs = params.toString();
  return apiRequest(`/catalog/templates${qs ? `?${qs}` : ''}`);
};

export const getCatalogTemplate = async (id) => {
  return apiRequest(`/catalog/templates/${id}`);
};

export const getCatalogTemplateEditHistory = async (id) => {
  const res = await apiRequest(`/catalog/templates/${encodeURIComponent(id)}/edit-history`);
  return res?.data ?? res;
};

export const createCatalogTemplate = async (payload) => {
  return apiRequest('/catalog/templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateCatalogTemplate = async (id, payload) => {
  return apiRequest(`/catalog/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteCatalogTemplate = async (id) => {
  return apiRequest(`/catalog/templates/${id}`, {
    method: 'DELETE',
  });
};

export const cloneCatalogTemplate = async (id) => {
  return apiRequest(`/catalog/templates/${id}/clone`, {
    method: 'POST',
  });
};

export const getCatalogTemplateSystemVariables = async () => {
  return apiRequest('/catalog/templates/system-variables');
};

export const previewCatalogTemplateHtml = async (payload) => {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const body = {
    catalog_id: payload.catalog_id,
    document_json: payload.document_json || payload.documentJson,
    template_json: payload.template_json || payload.templateJson,
    instance_json: payload.instance_json || payload.instanceJson,
    composition: payload.composition,
    preview_mode: payload.preview_mode || payload.previewMode,
  };
  if (payload.allow_synthetic !== undefined || payload.allowSynthetic !== undefined) {
    body.allow_synthetic = payload.allow_synthetic ?? payload.allowSynthetic;
  }
  const response = await fetch(`${API_URL}/catalog/templates/preview`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const text = await response.text();
  if (!response.ok) {
    let msg = text;
    try {
      const data = JSON.parse(text);
      msg = data.error || data.message || text;
    } catch {
      /* keep text */
    }
    throw new Error(msg || 'Preview failed');
  }
  return text;
};

export const listExtensionPurchases = async (query = {}) => {
  const p = new URLSearchParams();
  if (query.status) p.set('status', String(query.status));
  if (query.from) p.set('from', String(query.from));
  if (query.to) p.set('to', String(query.to));
  if (query.category) p.set('category', String(query.category));
  if (query.q) p.set('q', String(query.q));
  if (typeof query.affects_stock === 'boolean') {
    p.set('affects_stock', query.affects_stock ? 'true' : 'false');
  }
  if (typeof query.has_receipt === 'boolean') {
    p.set('has_receipt', query.has_receipt ? 'true' : 'false');
  }
  const qs = p.toString();
  return apiRequest(`/purchases${qs ? `?${qs}` : ''}`);
};

function appendOperationalPurchaseFormData(formData, payload) {
  formData.append('description', String(payload.description ?? ''));
  formData.append('category', String(payload.category ?? ''));
  formData.append('quantity', String(payload.quantity));
  formData.append('unit_cost', String(payload.unit_cost));
  formData.append('total_cost', String(payload.total_cost));
  if (payload.notes != null && payload.notes !== '') {
    formData.append('notes', String(payload.notes));
  }
  if (payload.purchased_at) {
    formData.append('purchased_at', String(payload.purchased_at));
  }
  if (payload.product_id) {
    formData.append('product_id', String(payload.product_id));
  }
  if (payload.sku_id) {
    formData.append('sku_id', String(payload.sku_id));
  }
  formData.append('affects_stock', payload.affects_stock ? 'true' : 'false');
}

export const createExtensionPurchase = async (payload, receiptFile = null) => {
  const url = '/purchases';
  if (receiptFile) {
    const formData = new FormData();
    appendOperationalPurchaseFormData(formData, payload);
    const name = receiptFile.name || 'receipt';
    formData.append('receipt', receiptFile, name);
    return workspaceMultipartPost(url, formData);
  }
  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateExtensionPurchase = async (purchaseId, payload, receiptFile = null) => {
  const url = `/purchases/${encodeURIComponent(purchaseId)}`;
  if (receiptFile) {
    const formData = new FormData();
    appendOperationalPurchaseFormData(formData, payload);
    const name = receiptFile.name || 'receipt';
    formData.append('receipt', receiptFile, name);
    return workspaceMultipartRequest(url, formData, 'PUT');
  }
  return apiRequest(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteExtensionPurchase = async (purchaseId) => {
  return apiRequest(`/purchases/${encodeURIComponent(purchaseId)}`, {
    method: 'DELETE',
  });
};

export const getPurchaseCategoriesSettings = async () => {
  return apiRequest('/purchases/settings/categories');
};

export const updatePurchaseCategoriesSettings = async (body) => {
  return apiRequest('/purchases/settings/categories', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const getPurchaseSuggestions = async (q = '') => {
  const qs = String(q || '').trim();
  return apiRequest(`/purchases/suggestions${qs ? `?q=${encodeURIComponent(qs)}` : ''}`);
};

/** Marks pending purchases as bought; the optional receipt is attached to every purchase made. */
export const completePurchases = async (payload, receiptFile = null) => {
  if (receiptFile) {
    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    formData.append('receipt', receiptFile, receiptFile.name || 'receipt');
    return workspaceMultipartPost('/purchases/complete', formData);
  }
  return apiRequest('/purchases/complete', { method: 'POST', body: JSON.stringify(payload) });
};

export const getPolaroidSizesSettings = async () => {
  return apiRequest('/polaroid-creator/settings/sizes');
};

export const updatePolaroidSizesSettings = async (body) => {
  return apiRequest('/polaroid-creator/settings/sizes', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/** Opens purchase receipt in a new tab (auth + workspace headers; blob stored in DB or legacy file). */
export const openExtensionPurchaseReceipt = async (purchaseId) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const response = await fetch(`${API_URL}/purchases/${encodeURIComponent(purchaseId)}/receipt`, {
    headers,
  });
  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    let msg = text || 'Request failed';
    try {
      const j = JSON.parse(text);
      if (j.error) msg = j.error;
      else if (j.message) msg = j.message;
    } catch (_) {
      /* use msg */
    }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const getWorkspaces = async () => {
  return apiRequest('/workspaces');
};

export const createWorkspace = async (data) => {
  return apiRequest('/workspaces', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const deleteWorkspace = async (id) => {
  return apiRequest(`/workspaces/${id}`, {
    method: 'DELETE',
  });
};

export const getMyProfile = async () => {
  return apiRequest('/auth/me');
};

export const updateMyProfile = async (data) => {
  return apiRequest('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const uploadPhoto = async (file) => {
  const token = getToken();
  const formData = new FormData();
  formData.append('photo', file);

  const response = await fetch(`${API_URL}/auth/me/photo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Upload failed');
  }

  return data;
};

export const getMedias = async () => {
  const now = Date.now();
  if (mediasInFlight && now - mediasCachedAt < MEDIAS_CACHE_MS) {
    return mediasInFlight;
  }
  mediasCachedAt = now;
  mediasInFlight = apiRequest('/medias').catch((err) => {
    invalidateMediasCache();
    throw err;
  });
  return mediasInFlight;
};

export const getMedia = async (id) => {
  return apiRequest(`/medias/${id}`);
};

export const fetchMediaFileBlob = async (id) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }

  const response = await fetch(`${API_URL}/medias/${encodeURIComponent(id)}/file`, {
    method: 'GET',
    headers,
  });

  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const text = await response.text();
    let msg = text || 'Request failed';
    try {
      const data = text ? JSON.parse(text) : {};
      msg = data.error || data.message || msg;
    } catch (_) {
      /* keep msg */
    }
    throw new Error(msg);
  }

  return response.blob();
};

export const uploadMedia = async (formData) => {
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (workspaceIdForApi) headers['X-Workspace-ID'] = workspaceIdForApi;

  const response = await fetch(`${API_URL}/medias`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Upload failed');
  }

  invalidateMediasCache();
  return data;
};

export const deleteMedia = async (id) => {
  const res = await apiRequest(`/medias/${id}`, {
    method: 'DELETE',
  });
  invalidateMediasCache();
  return res;
};

export const getMediaLinks = async (id) => {
  return apiRequest(`/medias/${id}/links`);
};

export const linkMedia = async (mediaId, metadata) => {
  const res = await apiRequest(`/medias/${mediaId}/link`, {
    method: 'POST',
    body: JSON.stringify({ media_id: mediaId, metadata }),
  });
  invalidateMediasCache();
  return res;
};

export const unlinkMedia = async (mediaId, metadataKey, metadataValue) => {
  const res = await apiRequest(`/medias/${mediaId}/unlink`, {
    method: 'POST',
    body: JSON.stringify({ metadata_key: metadataKey, metadata_value: metadataValue }),
  });
  invalidateMediasCache();
  return res;
};

export const updateMediaOrder = async (metadataKey, entityId, mediaOrders) => {
  const res = await apiRequest('/medias/order', {
    method: 'POST',
    body: JSON.stringify({
      metadata_key: metadataKey,
      entity_id: entityId,
      media_orders: mediaOrders,
    }),
  });
  invalidateMediasCache();
  return res;
};

export const updateMediaMetadata = async (mediaId, metadataKey, entityId, title, url, isPublic) => {
  const body = {
    metadata_key: metadataKey,
    entity_id: entityId,
    title: title ?? '',
    url: url ?? '',
  };
  if (typeof isPublic === 'boolean') {
    body.public = isPublic;
  }
  const res = await apiRequest(`/medias/${mediaId}/metadata`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  invalidateMediasCache();
  return res;
};

export const getCustomers = async () => {
  return apiRequest('/customers');
};

export const getCustomer = async (id) => {
  return apiRequest(`/customers/${id}`);
};

export const createCustomer = async (data) => {
  return apiRequest('/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateCustomer = async (id, data) => {
  return apiRequest(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteCustomer = async (id) => {
  return apiRequest(`/customers/${id}`, {
    method: 'DELETE',
  });
};

export const getOrders = async (query = {}) => {
  const q = new URLSearchParams();
  if (query.sort) q.set('sort', String(query.sort));
  if (query.order) q.set('order', String(query.order));
  if (typeof query.page === 'number') q.set('page', String(query.page));
  if (typeof query.page_size === 'number') q.set('page_size', String(query.page_size));
  const s = String(query.q || '').trim();
  if (s) q.set('q', s);
  if (query.status_filter === 'none') q.set('status_filter', 'none');
  const statuses = query.status;
  if (Array.isArray(statuses)) {
    for (const s of statuses) {
      const t = String(s).trim();
      if (t) q.append('status', t);
    }
  }
  if (query.scope === 'customer' || query.scope === 'internal') q.set('scope', query.scope);
  if (Array.isArray(query.order_type_id)) {
    for (const t of query.order_type_id) {
      const v = String(t).trim();
      if (v) q.append('order_type_id', v);
    }
  }
  if (query.due === 'overdue' || query.due === 'week') q.set('due', query.due);
  if (query.summary) q.set('summary', '1');
  const qs = q.toString();
  return apiRequest(`/orders${qs ? `?${qs}` : ''}`);
};

export const getOrder = async (id) => {
  return apiRequest(`/orders/${id}`);
};

/** Soft-delete (hide) an order from lists when it has no line items; backend clears client link and some medias. */
export const softDeleteOrder = async (id) => {
  return apiRequest(`/orders/${id}`, {
    method: 'DELETE',
  });
};

export const createOrder = async (data) => {
  return apiRequest('/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const createOrderFromSource = async (data) => {
  return apiRequest('/orders/create-from-order', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/** Merge data from another order into an existing order (adds lines, appends history, etc.). */
export const importOrderDataInto = async (orderId, data) => {
  return apiRequest(`/orders/${orderId}/import-from`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const listOrderStatusDefinitions = async () => {
  return apiRequest('/orders/workflow/status-definitions');
};

export const createOrderStatusDefinition = async (body) => {
  return apiRequest('/orders/workflow/status-definitions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const updateOrderStatusDefinition = async (id, body) => {
  return apiRequest(`/orders/workflow/status-definitions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deleteOrderStatusDefinition = async (id) => {
  return apiRequest(`/orders/workflow/status-definitions/${id}`, {
    method: 'DELETE',
  });
};

/** When reassignToStatusId is set, moves orders/history to that status and removes the definition (and type links). */
export const removeOrderStatusDefinition = async (id, reassignToStatusId) => {
  const to = (reassignToStatusId || '').trim();
  if (to) {
    return apiRequest(`/orders/workflow/status-definitions/${id}/delete`, {
      method: 'POST',
      body: JSON.stringify({ reassign_to_status_id: to }),
    });
  }
  return deleteOrderStatusDefinition(id);
};

export const listOrderTypes = async (activeOnly = false) => {
  const q = activeOnly ? '?active_only=1' : '';
  return apiRequest(`/orders/workflow/order-types${q}`);
};

export const changeOrderType = async (orderId, body) => {
  return apiRequest(`/orders/${orderId}/change-order-type`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const createOrderType = async (body) => {
  return apiRequest('/orders/workflow/order-types', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const updateOrderType = async (id, body) => {
  return apiRequest(`/orders/workflow/order-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deleteOrderType = async (id) => {
  return apiRequest(`/orders/workflow/order-types/${id}`, {
    method: 'DELETE',
  });
};

export const getOrderTypeStatuses = async (typeId) => {
  return apiRequest(`/orders/workflow/order-types/${typeId}/statuses`);
};

export const replaceOrderTypeStatuses = async (typeId, body) => {
  return apiRequest(`/orders/workflow/order-types/${typeId}/statuses`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const removeOrderTypeStatus = async (typeId, statusId, body) => {
  return apiRequest(`/orders/workflow/order-types/${typeId}/statuses/${statusId}/remove`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
};

export const updateOrder = async (id, data) => {
  return apiRequest(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

const paymentEntryMultipartRequest = async (method, path, body, receiptFile) => {
  const fd = new FormData();
  fd.append('data', JSON.stringify(body));
  if (receiptFile) {
    fd.append('receipt', receiptFile);
  }
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: fd,
    });
  } catch (error) {
    console.error('Network error:', error);
    throw new Error('Network error: ' + error.message);
  }
  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (parseError) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }
  return data;
};

export const createOrderPaymentEntry = async (orderId, body, receiptFile) => {
  if (receiptFile) {
    return paymentEntryMultipartRequest('POST', `/orders/${orderId}/payment-entries`, body, receiptFile);
  }
  return apiRequest(`/orders/${orderId}/payment-entries`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const updateOrderPaymentEntry = async (orderId, entryId, body, receiptFile) => {
  if (receiptFile) {
    return paymentEntryMultipartRequest('PATCH', `/orders/${orderId}/payment-entries/${entryId}`, body, receiptFile);
  }
  return apiRequest(`/orders/${orderId}/payment-entries/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

/** Opens receipt in a new tab; URL bar shows /orders/{orderId}/payments/{entryId}/receipt (SPA). */
export const openOrderPaymentEntryReceipt = (orderId, entryId) => {
  const path = `/orders/${orderId}/payments/${entryId}/receipt`;
  const url = `${window.location.origin}${path}`;
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) {
    return Promise.reject(new Error('Popup blocked'));
  }
  return Promise.resolve();
};

export const deleteOrderPaymentEntry = async (orderId, entryId) => {
  return apiRequest(`/orders/${orderId}/payment-entries/${entryId}`, {
    method: 'DELETE',
  });
};

export const patchOrderStatusHistory = async (orderId, historyId, body) => {
  return apiRequest(`/orders/${orderId}/status-history/${historyId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export const deleteOrderStatusHistory = async (orderId, historyId) => {
  return apiRequest(`/orders/${orderId}/status-history/${historyId}`, {
    method: 'DELETE',
  });
};

export const approveOrder = async (id) => {
  return apiRequest(`/orders/${id}/approve`, {
    method: 'POST',
  });
};

export const addOrderLine = async (orderId, data) => {
  return apiRequest(`/orders/${orderId}/lines`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const addOrderLineItem = async (orderId, lineId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateOrderLine = async (orderId, lineId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteOrderLine = async (orderId, lineId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}`, {
    method: 'DELETE',
  });
};

export const updateOrderLineItem = async (orderId, lineId, itemId, data) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/** Grava a ordem dos itens de uma linha; itemIds deve listar todos os itens da linha. */
export const reorderOrderLineItems = async (orderId, lineId, itemIds) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/order`, {
    method: 'PUT',
    body: JSON.stringify({ item_ids: itemIds }),
  });
};

export const deleteOrderLineItem = async (orderId, lineId, itemId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}`, {
    method: 'DELETE',
  });
};

export const getOrderCustomizationFields = async (orderId) => {
  return apiRequest(`/orders/${orderId}/customization-fields`);
};

export const upsertOrderCustomizationFields = async (orderId, fields) => {
  return apiRequest(`/orders/${orderId}/customization-fields`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
};

export const upsertOrderLineItemCustomizationFields = async (orderId, lineId, itemId, fields) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}/customization-fields`, {
    method: 'PUT',
    body: JSON.stringify({ fields }),
  });
};

export const deleteOrderLineItemCustomizationField = async (orderId, lineId, itemId, fieldId) => {
  return apiRequest(`/orders/${orderId}/lines/${lineId}/items/${itemId}/customization-fields/${fieldId}`, {
    method: 'DELETE',
  });
};

export const getOrderPublicForm = async (workspaceId, token) => {
  return publicApiRequest(`/public/orders/form/${workspaceId}/${token}`);
};

export const submitOrderPublicForm = async (workspaceId, token, data) => {
  return publicApiRequest(`/public/orders/form/${workspaceId}/${token}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const uploadOrderPublicFormMedia = async (workspaceId, token, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(
    `${API_URL}/public/orders/form/${encodeURIComponent(workspaceId)}/${encodeURIComponent(token)}/media`,
    {
      method: 'POST',
      body: formData,
    }
  );
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (!response.ok) {
      throw new Error(text || 'Upload failed');
    }
    throw new Error('Invalid response from server');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Upload failed');
  }
  return data;
};

export const getQuickIntakeOrderTypes = async (workspaceId) => {
  return publicApiRequest(`/public/orders/quick-intake/${encodeURIComponent(workspaceId)}/order-types`);
};

export const submitQuickIntakeOrder = async (workspaceId, formData) => {
  const response = await fetch(
    `${API_URL}/public/orders/quick-intake/${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      body: formData,
    }
  );
  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }
  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }
  return data;
};

export const getDashboardOverview = async () => {
  return apiRequest('/dashboard/overview');
};

export const listApiKeys = async () => {
  return apiRequest('/api-keys');
};

export const createApiKey = async (data) => {
  return apiRequest('/api-keys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const revokeApiKey = async (id) => {
  return apiRequest(`/api-keys/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
};

export const getSettings = async () => {
  return apiRequest('/settings');
};

export const updateSettings = async (data) => {
  return apiRequest('/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const getExtensions = async () => {
  return apiRequest('/extensions');
};

export const enableExtension = async (id) => {
  return apiRequest(`/extensions/${encodeURIComponent(id)}/enable`, { method: 'POST' });
};

export const disableExtension = async (id) => {
  return apiRequest(`/extensions/${encodeURIComponent(id)}/disable`, { method: 'POST' });
};

export const getDesktopReleases = async () => {
  const res = await apiRequest('/desktop/releases');
  return res?.data ?? res;
};

export const submitImprovementSuggestion = async (formData) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }

  const response = await fetch(`${API_URL}/improvement-suggestions`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }

  const text = await response.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    if (!response.ok) {
      throw new Error(text || 'Request failed');
    }
    throw new Error('Invalid response from server');
  }

  if (!response.ok) {
    throw new Error(data.error || data.message || text || 'Request failed');
  }

  return data;
};

export const getAdminImprovementSuggestions = async () => {
  return apiRequest('/admin/improvement-suggestions');
};

export const deleteAdminImprovementSuggestion = async (id) => {
  return apiRequest(`/admin/improvement-suggestions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
};

export const fetchAdminImprovementSuggestionImage = async (id, kind = 'screenshot') => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const suffix = kind === 'extra' ? 'extra-image' : 'screenshot';
  const response = await fetch(
    `${API_URL}/admin/improvement-suggestions/${encodeURIComponent(id)}/${suffix}`,
    { headers }
  );
  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error('Failed to load image');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const downloadDesktopRelease = async (version, platform) => {
  const platformFilenames = {
    windows: 'eagle-desktop.exe',
    windows_msi: `EagleDesktop-${version}.msi`,
    linux: 'eagle-desktop',
  };
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (workspaceIdForApi) {
    headers['X-Workspace-ID'] = workspaceIdForApi;
  }
  const response = await fetch(
    `${API_URL}/desktop/releases/${encodeURIComponent(version)}/download/${encodeURIComponent(platform)}`,
    { headers }
  );
  if (response.status === 401) {
    removeToken();
    redirectToLoginAfterUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    let msg = text || 'Request failed';
    try {
      const j = JSON.parse(text);
      if (j.error) msg = j.error;
      else if (j.message) msg = j.message;
    } catch (_) {
      /* use msg */
    }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const cd = response.headers.get('Content-Disposition');
  let filename = platformFilenames[platform] || `eagle-desktop-${version}`;
  if (cd && cd.includes('filename=')) {
    const m = cd.match(/filename="([^"]+)"/) || cd.match(/filename=([^;\s]+)/);
    if (m) {
      filename = m[1].replace(/"/g, '');
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
